import { useState } from "react";
import { CloseOutlined, WhatsAppOutlined, SendOutlined } from "@ant-design/icons";

// Message templates for different order statuses
const MESSAGE_TEMPLATES = {
  orderConfirmation: {
    label: "Order Confirmation",
    icon: "✅",
    color: "bg-green-50 border-green-200 hover:bg-green-100",
    template: (order) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `Your order #${order.OrderToken} has been confirmed! ✅\n\n` +
      `📦 Order Details:\n` +
      `• Items: ${order.Items?.length || 0} item(s)\n` +
      `• Total: Rs. ${Math.round((order.AmountWithGst || 0) + (order.DeliveryCharges || 0)).toLocaleString()}\n` +
      `• Payment: ${order.PaymentType || 'N/A'}\n\n` +
      (order.OrderType === 'Delivery' 
        ? `🚚 Delivery Time: ${order.DeliveryTime || 'TBD'} minutes\n` +
          `📍 Address: ${order.CustomerDetails?.DeliveryAddress || 'N/A'}\n\n`
        : `🏪 Pickup from: ${order.BranchName || 'Our store'}\n\n`) +
      `We're preparing your order with care! 🍽️\n\n` +
      `Thank you for choosing us! 😊`
  },
  
  orderDispatched: {
    label: "Order Dispatched",
    icon: "🚚",
    color: "bg-purple-50 border-purple-200 hover:bg-purple-100",
    template: (order, riderName) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `Great news! Your order #${order.OrderToken} is on its way! 🚚\n\n` +
      (riderName 
        ? `👤 Rider: ${riderName}\n` 
        : '') +
      `⏱️ Expected in: ${order.DeliveryTime || 'Shortly'} minutes\n` +
      `📍 Delivering to: ${order.CustomerDetails?.DeliveryAddress || 'Your location'}\n\n` +
      `Please keep your phone handy for rider contact! 📱\n\n` +
      `Thank you! 😊`
  },
  
  orderDelivered: {
    label: "Order Delivered",
    icon: "🎉",
    color: "bg-blue-50 border-blue-200 hover:bg-blue-100",
    template: (order) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `Your order #${order.OrderToken} has been delivered! 🎉\n\n` +
      `We hope you enjoy your meal! 🍽️\n\n` +
      `❤️ Thank you for choosing us!\n` +
      `We'd love to serve you again soon! 😊\n\n` +
      `Rate your experience: [Link to feedback]`
  },
  
  orderCancelled: {
    label: "Order Cancelled",
    icon: "❌",
    color: "bg-red-50 border-red-200 hover:bg-red-100",
    template: (order, reason) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `We regret to inform you that order #${order.OrderToken} has been cancelled. ❌\n\n` +
      (reason 
        ? `📝 Reason: ${reason}\n\n` 
        : '') +
      `We sincerely apologize for any inconvenience caused. 🙏\n\n` +
      `If you have any questions, please feel free to contact us.\n\n` +
      `We hope to serve you again soon! 😊`
  },
  
  delayNotification: {
    label: "Delay Notification",
    icon: "⏰",
    color: "bg-amber-50 border-amber-200 hover:bg-amber-100",
    template: (order, delayMinutes) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `We wanted to update you about order #${order.OrderToken}.\n\n` +
      `⏰ There's a slight delay of approximately ${delayMinutes || '15'} minutes.\n\n` +
      `We're working hard to get your order to you ASAP! 🏃‍♂️\n\n` +
      `Thank you for your patience and understanding! 🙏😊`
  },
  
  readyForPickup: {
    label: "Ready for Pickup",
    icon: "🎁",
    color: "bg-teal-50 border-teal-200 hover:bg-teal-100",
    template: (order) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `Your order #${order.OrderToken} is ready for pickup! 🎁\n\n` +
      `🏪 Pickup Location: ${order.BranchName || 'Our store'}\n` +
      `⏰ You can collect your order anytime during our business hours.\n\n` +
      `Please bring this message or your order number.\n\n` +
      `See you soon! 😊`
  },
  
  custom: {
    label: "Custom Message",
    icon: "✍️",
    color: "bg-gray-50 border-gray-200 hover:bg-gray-100",
    template: (order) => 
      `Hello ${order.CustomerDetails?.FullName}! 👋\n\n` +
      `Regarding your order #${order.OrderToken}:\n\n`
  }
};

function formatPakNumber(number) {
  // remove spaces, dashes, etc.
  let clean = number.replace(/\D/g, '');

  // if starts with 0 (local format)
  if (clean.startsWith('0')) {
    clean = '92' + clean.slice(1);
  }

  // if already starts with 92
  else if (!clean.startsWith('92')) {
    clean = '92' + clean;
  }

  return clean;
}

export default function WhatsAppMessageModal({ 
  order, 
  onClose, 
  currentRider 
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState({
    reason: "",
    delayMinutes: ""
  });

  const phoneNumber = formatPakNumber(order.CustomerDetails?.MobileNumber);

  const handleTemplateSelect = (templateKey) => {
    setSelectedTemplate(templateKey);
    
    // Pre-fill custom message if custom template is selected
    if (templateKey === 'custom') {
      setCustomMessage(MESSAGE_TEMPLATES.custom.template(order));
    }
  };

  const generateMessage = () => {
    if (!selectedTemplate) return "";
    
    const template = MESSAGE_TEMPLATES[selectedTemplate];
    
    switch (selectedTemplate) {
      case 'orderDispatched':
        return template.template(order, currentRider?.Name);
      case 'orderCancelled':
        return template.template(order, additionalInfo.reason);
      case 'delayNotification':
        return template.template(order, additionalInfo.delayMinutes);
      case 'custom':
        return customMessage;
      default:
        return template.template(order);
    }
  };

  const handleSendWhatsApp = () => {
    if (!phoneNumber) {
      alert("No phone number available for this customer.");
      return;
    }

    const message = generateMessage();
    if (!message.trim()) {
      alert("Please enter a message.");
      return;
    }

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // WhatsApp URL - works on both mobile and desktop
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Close modal after opening WhatsApp
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" 
      style={{ zIndex: 200 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <CloseOutlined />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <WhatsAppOutlined className="text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Send WhatsApp Message</h2>
              <p className="text-sm text-green-100">
                To: {order.CustomerDetails?.FullName} ({phoneNumber || 'No number'})
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Template Selection */}
          {!selectedTemplate ? (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Choose a Message Template
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(MESSAGE_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    onClick={() => handleTemplateSelect(key)}
                    className={`p-4 border-2 rounded-xl text-left transition-all hover:shadow-md ${template.color}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{template.icon}</span>
                      <span className="font-semibold text-gray-800">
                        {template.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* Back Button */}
              <button
                onClick={() => {
                  setSelectedTemplate(null);
                  setCustomMessage("");
                  setAdditionalInfo({ reason: "", delayMinutes: "" });
                }}
                className="mb-4 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                ← Change Template
              </button>

              {/* Additional Input Fields for Specific Templates */}
              {selectedTemplate === 'orderCancelled' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cancellation Reason (Optional)
                  </label>
                  <input
                    type="text"
                    value={additionalInfo.reason}
                    onChange={(e) => setAdditionalInfo({ ...additionalInfo, reason: e.target.value })}
                    placeholder="e.g., Out of stock, Customer request"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              )}

              {selectedTemplate === 'delayNotification' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delay Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={additionalInfo.delayMinutes}
                    onChange={(e) => setAdditionalInfo({ ...additionalInfo, delayMinutes: e.target.value })}
                    placeholder="e.g., 15"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Message Preview/Editor */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedTemplate === 'custom' ? 'Your Message' : 'Message Preview'}
                </label>
                <textarea
                  value={selectedTemplate === 'custom' ? customMessage : generateMessage()}
                  onChange={selectedTemplate === 'custom' ? (e) => setCustomMessage(e.target.value) : undefined}
                  readOnly={selectedTemplate !== 'custom'}
                  rows={12}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm ${
                    selectedTemplate === 'custom' 
                      ? 'focus:ring-2 focus:ring-green-500 focus:border-transparent' 
                      : 'bg-gray-50'
                  }`}
                  placeholder={selectedTemplate === 'custom' ? 'Type your custom message here...' : ''}
                />
              </div>

              {/* Character Count */}
              <p className="text-xs text-gray-500 mb-4">
                Message length: {generateMessage().length} characters
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {selectedTemplate && (
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSendWhatsApp}
              disabled={!phoneNumber}
              className="px-6 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors shadow-md hover:shadow-lg disabled:cursor-not-allowed"
            >
              <WhatsAppOutlined className="text-lg" />
              Open in WhatsApp
              <SendOutlined />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}